import { loadSupersetFilters } from '../charts/superset-bundle.js';

/**
 * The Alpine half of <x-filters.checkbox>, <x-filters.radio> and <x-filters.boolean>
 * (App\View\Components\Filters\ChoiceFilter): Superset's Checkbox, RadioButton and Boolean filters
 * from the blink-superset bundle, bound to a Livewire property through x-modelable.
 *
 * cf_value IS the bound property: Livewire moves it when the server does, and the host moves it when
 * the reader ticks. Its form is the kind's: a checkbox's list of ticked values, a radio's one value, a
 * Boolean's ['true'] (or ['false'] for a filter for FALSE values), and null for none. The host sends
 * values only, never the DataMask's extraFormData, which is Superset's query clause for Superset's
 * backend: the server builds its own, with the values bound.
 *
 * The inputs are native, so the browser's own keyboard contract holds (Space ticks, the arrow keys
 * move through a radio group) and nothing here handles a key. They are also controlled by the host:
 * after a reader's tick the host mounts again with the value it wrote, or the box would snap back.
 * blink-superset-bundle.md, "The choice hosts", says why each rule below exists.
 */

// The components key their rows by a column name and put it in their query clause, which the host
// never sends; the column a report filters is the server's, named in the filter itself.
const COLUMN = 'value';

const MOUNTS = {
    checkbox: ['mountCheckboxFilter', 'unmountCheckboxFilter'],
    radio: ['mountRadioButtonFilter', 'unmountRadioButtonFilter'],
    boolean: ['mountBooleanFilter', 'unmountBooleanFilter'],
};

const isNonEmptyString = (value) => typeof value === 'string' && value !== '';

/** The bound value as the adapter takes it for this kind, or null; readable is false for another shape. */
function toValue(kind, value, checked) {
    if (value === null || value === undefined) return { value: null, readable: true };
    if (kind === 'radio') return isNonEmptyString(value) ? { value, readable: true } : { value: null, readable: false };
    if (kind === 'boolean') {
        return Array.isArray(value) && value.length === 1 && value[0] === checked[0]
            ? { value: [...checked], readable: true }
            : { value: null, readable: false };
    }
    if (Array.isArray(value) && value.every(isNonEmptyString)) return { value: value.length ? [...value] : null, readable: true };
    return { value: null, readable: false };
}

/** The reader's value in a mask, in the bound property's form: null for a clear. */
function fromDataMask(kind, dataMask) {
    const value = dataMask?.filterState?.value;
    if (kind === 'radio') return isNonEmptyString(value) ? value : null;
    const strings = Array.isArray(value) ? value.filter(isNonEmptyString) : [];
    return strings.length ? strings : null;
}

function sameValue(a, b) {
    const key = (value) => (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
        ? null
        : JSON.stringify(value));
    return key(a) === key(b);
}

/**
 * The options the server rendered. Read inside cf_mount()'s try, so a script that does not parse
 * shows the failed state and logs, rather than a filter with nothing in it.
 */
function readOptions(root) {
    const options = JSON.parse(root.querySelector('script[data-choice-filter-options]')?.textContent || '[]');
    return Array.isArray(options) ? options : [];
}

function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

export default function ChoiceFilter(config = {}) {
    const kind = config.kind;
    const [mountName, unmountName] = MOUNTS[kind] ?? [];
    const checked = config.forFalseValues === true ? ['false'] : ['true'];
    const ids = config.ids ?? {};
    const text = config.text ?? {};

    // In the closure, not reactive: Alpine would deep-proxy the bundle and the React root it drives.
    let bundle = null;
    let observers = [];
    let alive = true;
    let mounted = false;
    // The value this host last wrote, until $watch hands it back.
    let sent;
    let hasSent = false;
    // The value the inputs hold, so a $watch for it is not taken for a change.
    let mountedFor = null;

    return {
        cf_value: null,
        cf_state: 'loading',
        cf_announcement: '',
        cf_text: {
            loadFailed: text.loadFailed ?? 'This filter could not be loaded.',
            retry: text.retry ?? 'Retry',
        },

        init() {
            // Two hosts with one id share their legend, hint and error ids, so each would describe
            // the other's inputs.
            const twins = document.querySelectorAll(`[data-choice-filter="${config.id}"]`);
            if (twins.length > 1 && twins[0] !== this.$root) {
                console.error('Choice filter id is not unique on the page: pass a distinct id', { id: config.id });
            }

            this.$watch('cf_value', (value) => this.cf_follow(value));

            // Both are server-rendered on the morphable root, while the inputs they describe are
            // React's: a validation error, and the options after a render.
            const invalid = new MutationObserver(() => this.cf_mount(this.cf_value));
            invalid.observe(this.$root, { attributes: true, attributeFilter: ['data-invalid'] });
            observers = [invalid];

            const options = this.$root.querySelector('script[data-choice-filter-options]');
            if (options) {
                const moved = new MutationObserver(() => this.cf_mount(this.cf_value));
                moved.observe(options, { childList: true, characterData: true, subtree: true });
                observers.push(moved);
            }

            // aria-invalid (and the Boolean's description) are attributes React does not manage on
            // the inputs it owns, so they are put back whenever it renders new ones.
            const rendered = new MutationObserver(() => this.cf_wire());
            rendered.observe(this.$refs.mount, { childList: true, subtree: true });
            observers.push(rendered);

            // A microtask later: x-modelable hands over the bound value in a microtask it queues
            // after init(), and a chunk already fetched would otherwise mount an empty filter first.
            queueMicrotask(() => this.cf_load());
        },

        destroy() {
            alive = false;
            observers.forEach((observer) => observer.disconnect());
            if (bundle && mounted) this.cf_unmount();
            bundle = null;
        },

        cf_load({ refocus = false } = {}) {
            if (! alive) return;
            this.cf_state = 'loading';

            loadSupersetFilters()
                .then((loaded) => {
                    if (! alive) return;
                    bundle = loaded;
                    this.cf_state = 'ready';
                    if (! this.cf_mount(this.cf_value)) return;
                    // A recovered filter must not leave "could not be loaded" behind in the alert.
                    this.cf_announcement = '';
                    if (refocus) this.$refs.mount.querySelector('input')?.focus();
                })
                .catch((error) => this.cf_fail(error, refocus));
        },

        cf_retry() {
            this.cf_load({ refocus: true });
        },

        cf_fail(error, refocus = false) {
            console.error('Choice filter failed to load', { id: config.id, kind, error });
            bundle = null;
            mounted = false;
            this.cf_state = 'failed';
            // Cleared first and set on the next frame, so a second failure in a row is spoken again.
            this.cf_announcement = '';
            requestAnimationFrame(() => { this.cf_announcement = this.cf_text.loadFailed; });
            // Pressing Retry hid the button; a failure brings it back, and focus with it.
            if (refocus) this.$nextTick(() => this.$refs.retry?.focus());
        },

        cf_unmount() {
            bundle?.[unmountName]?.(this.$refs.mount);
            mounted = false;
        },

        /** Mounts, or re-renders in place, for a bound value. Returns whether the filter is up. */
        cf_mount(value) {
            if (! bundle) return false;

            const bound = toValue(kind, value, checked);
            if (! bound.readable) console.error('Choice filter value is not one it can show', { id: config.id, value });

            const invalid = this.$root.dataset.invalid === 'true';

            try {
                bundle[mountName](this.$refs.mount, this.cf_mountOptions(bound.value, invalid));
            } catch (error) {
                // The adapter registers its instance before it renders; left behind, Retry would
                // re-render it in place.
                this.cf_unmount();
                this.cf_fail(error);
                return false;
            }

            mounted = true;
            mountedFor = bound.value;
            this.cf_wire(invalid);

            return true;
        },

        cf_mountOptions(value, invalid) {
            const options = {
                id: config.id,
                column: COLUMN,
                filterState: { value, validateStatus: invalid ? 'error' : undefined },
                onDataMask: (dataMask, change) => this.cf_receive(dataMask, change),
            };

            if (kind === 'boolean') {
                // Labelled by the filter's name, which the box's own <label> then carries as its name.
                return {
                    ...options,
                    formData: { booleanFilterValue: config.forFalseValues === true, useFilterNameAsLabel: true, filterName: config.label },
                };
            }

            return { ...options, options: readOptions(this.$root) };
        },

        /**
         * Marks every input invalid while the server says so, which antd's inputs cannot learn from
         * Blade. A group is described by its fieldset; the Boolean checkbox, with no group, is
         * described by the hint and error itself.
         */
        cf_wire(invalid = this.$root.dataset.invalid === 'true') {
            this.$refs.mount.querySelectorAll('input').forEach((input) => {
                if (kind === 'boolean') setAttribute(input, 'aria-describedby', ids.describedBy);

                if (invalid) setAttribute(input, 'aria-invalid', 'true');
                else if (input.getAttribute('aria-invalid') !== null) input.removeAttribute('aria-invalid');
            });
        },

        /** Whether anything is picked: what shows the radio host's Clear button. */
        cf_hasValue() {
            return toValue(kind, this.cf_value, checked).value !== null;
        },

        /**
         * Empties a radio group, which its radios cannot do. Focus moves to the first radio: the Clear
         * button hides once nothing is picked, and focus left on it would fall to <body>. A mount
         * that fails hides the radios too, so focus goes to Retry instead.
         */
        cf_clear() {
            sent = null;
            hasSent = true;
            this.cf_value = null;
            if (this.cf_mount(null)) this.$refs.mount.querySelector('input')?.focus();
            else this.$nextTick(() => this.$refs.retry?.focus());
        },

        cf_receive(dataMask, change) {
            // The component echoing the value it was given: nothing new, and writing it back could
            // undo a newer tick on a slow round trip.
            if (change?.auto) return;

            const value = fromDataMask(kind, dataMask);
            sent = value;
            hasSent = true;
            this.cf_value = value;

            // After React's event: the inputs are controlled, so they show the tick only once they are
            // mounted with it, and the component's next echo of it is then not taken for the reader's.
            queueMicrotask(() => {
                if (alive && bundle) this.cf_mount(value);
            });
        },

        cf_follow(value) {
            const own = hasSent && sameValue(value, sent);
            hasSent = false;
            sent = undefined;
            if (own || ! bundle || (mounted && sameValue(toValue(kind, value, checked).value, mountedFor))) return;

            this.cf_mount(value);
        },
    };
}
